/**
 * The country vocabulary every location field shares. Values are ISO 3166-1 alpha-2 so
 * `cross_border_transfer` can compare a vendor's processing countries against the tenant's
 * `home_region`; labels are the full country name.
 */
export const COUNTRY_OPTIONS = [
  { value: 'ae', label: 'United Arab Emirates' },
  { value: 'ar', label: 'Argentina' },
  { value: 'at', label: 'Austria' },
  { value: 'au', label: 'Australia' },
  { value: 'bd', label: 'Bangladesh' },
  { value: 'be', label: 'Belgium' },
  { value: 'bg', label: 'Bulgaria' },
  { value: 'bh', label: 'Bahrain' },
  { value: 'br', label: 'Brazil' },
  { value: 'ca', label: 'Canada' },
  { value: 'ch', label: 'Switzerland' },
  { value: 'cl', label: 'Chile' },
  { value: 'cn', label: 'China' },
  { value: 'co', label: 'Colombia' },
  { value: 'cy', label: 'Cyprus' },
  { value: 'cz', label: 'Czechia' },
  { value: 'de', label: 'Germany' },
  { value: 'dk', label: 'Denmark' },
  { value: 'eg', label: 'Egypt' },
  { value: 'ee', label: 'Estonia' },
  { value: 'es', label: 'Spain' },
  { value: 'et', label: 'Ethiopia' },
  { value: 'fi', label: 'Finland' },
  { value: 'fr', label: 'France' },
  { value: 'gb', label: 'United Kingdom' },
  { value: 'gh', label: 'Ghana' },
  { value: 'gr', label: 'Greece' },
  { value: 'hk', label: 'Hong Kong' },
  { value: 'hr', label: 'Croatia' },
  { value: 'hu', label: 'Hungary' },
  { value: 'id', label: 'Indonesia' },
  { value: 'ie', label: 'Ireland' },
  { value: 'il', label: 'Israel' },
  { value: 'in', label: 'India' },
  { value: 'is', label: 'Iceland' },
  { value: 'it', label: 'Italy' },
  { value: 'jo', label: 'Jordan' },
  { value: 'jp', label: 'Japan' },
  { value: 'ke', label: 'Kenya' },
  { value: 'kr', label: 'South Korea' },
  { value: 'kw', label: 'Kuwait' },
  { value: 'lb', label: 'Lebanon' },
  { value: 'lt', label: 'Lithuania' },
  { value: 'lu', label: 'Luxembourg' },
  { value: 'lv', label: 'Latvia' },
  { value: 'ma', label: 'Morocco' },
  { value: 'mt', label: 'Malta' },
  { value: 'mx', label: 'Mexico' },
  { value: 'my', label: 'Malaysia' },
  { value: 'ng', label: 'Nigeria' },
  { value: 'nl', label: 'Netherlands' },
  { value: 'no', label: 'Norway' },
  { value: 'nz', label: 'New Zealand' },
  { value: 'om', label: 'Oman' },
  { value: 'pe', label: 'Peru' },
  { value: 'ph', label: 'Philippines' },
  { value: 'pk', label: 'Pakistan' },
  { value: 'pl', label: 'Poland' },
  { value: 'pt', label: 'Portugal' },
  { value: 'qa', label: 'Qatar' },
  { value: 'ro', label: 'Romania' },
  { value: 'rs', label: 'Serbia' },
  { value: 'ru', label: 'Russia' },
  { value: 'sa', label: 'Saudi Arabia' },
  { value: 'se', label: 'Sweden' },
  { value: 'sg', label: 'Singapore' },
  { value: 'si', label: 'Slovenia' },
  { value: 'sk', label: 'Slovakia' },
  { value: 'th', label: 'Thailand' },
  { value: 'tr', label: 'Turkey' },
  { value: 'tw', label: 'Taiwan' },
  { value: 'tz', label: 'Tanzania' },
  { value: 'ua', label: 'Ukraine' },
  { value: 'ug', label: 'Uganda' },
  { value: 'us', label: 'United States' },
  { value: 'vn', label: 'Vietnam' },
  { value: 'za', label: 'South Africa' },
  { value: 'other', label: 'Other' },
] as const;

export type CountryValue = (typeof COUNTRY_OPTIONS)[number]['value'];

/** Mutable copy, because Mantine's `data` prop rejects the readonly tuple. */
export const countrySelectData = COUNTRY_OPTIONS.map(({ value, label }) => ({
  value,
  label,
}));

const COUNTRY_LABELS = new Map<string, string>(
  COUNTRY_OPTIONS.map(({ value, label }) => [value, label]),
);

/** Display name for a stored country value; falls back to the raw value for a retired/unknown code. */
export function countryLabel(value: string): string {
  return COUNTRY_LABELS.get(value) ?? value;
}

/** The display names for a list of stored country values, in the order given. */
export function countryLabels(values: readonly string[]): string[] {
  return values.map(countryLabel);
}

/** Country values as the one-line list the Trust Center and the vendor register print. */
export function formatCountryList(values: readonly string[]): string {
  return countryLabels(values).join(', ');
}
